// ?modal=line-job&lineJob=<id> (finance-real, the user 2026-10-22): one job, one line item for one period, in one drawer
// (DD-045, DD-051), edited in place and saved as you type (useFieldAutosave, the user: "it auto saves with debounce"):
// its name, description, period (How it bills, and its days in one date-range picker with Monthly inside, PeriodPicker), price,
// margin % and Pays all to the team, which lifts Setup's ceiling off it (its margin % may then be 0), and, but on a one-off,
// Done when: when its period ends or when it is marked done. Off the own client, a list price and a discount % sit above the
// price: typed together they fill it, and the list price is saved with it; its price stays the net every figure reads.
// The Saving… mark sits by its status, the platform's; a save the server refuses (a margin % under Setup's
// floor, a proposed quote's job's words or price) shows its reason under the field and keeps what was typed. Its status
// is the platform's status dropdown (JobStatusBadge): Draft, Agreed, Active, Paused, Done, Dropped, with Quoting and
// Invoiced set by its quote and its invoice; Active from Draft or Agreed asks the day it starts. When its series makes the next one, and the claims
// the team makes on it, which the agency records here for each person: a person and an amount, several per job, together
// within its cap (the price less its margin %, never above Setup's ceiling); a claim is pressed open to change its person
// and amount or to remove it (ClaimsList, shared with the invoice drawer, the user 2026-10-23). What is free to claim, the claims' total and
// the margin are re-read from the server after each write (TD-102). The quote that proposed it, and the invoice it is on
// once invoiced, open in place of this drawer, each named as everywhere (its title, invoiceName) with its status the shared
// read-only pill. Its title is the job named in one string, "name · period" (jobLabel), so two periods of a series never
// read the same. A flag to review reads amber on its price and margin.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { SpinnerIcon } from "@/components/icons";
import { Button } from "@/components/ui/Button";
import { Callout } from "@/components/ui/Callout";
import { DayField } from "@/components/ui/DayField";
import type { DropdownMenuEntry } from "@/components/ui/DropdownMenu";
import { FormField, inputClassName } from "@/components/ui/FormField";
import { MoneyText } from "@/components/ui/MoneyText";
import { OptionDropdown, type DropdownOption } from "@/components/ui/OptionDropdown";
import { RowMenu } from "@/components/ui/RowMenu";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { minorToInput, parseMoneyMinor } from "@/utils/currency";
import { FinanceDrawer } from "../components/FinanceDrawer";
import { useFieldAutosave } from "../hooks/useFieldAutosave";
import type { JobDoneWhen, LineJob, LineJobPatch, LineJobPickedStatus, Person, QuoteBundle } from "../types";
import { prefixed } from "../utils/format";
import { invoiceDateLine, jobLabel } from "../utils/names";
import { ClaimsList } from "./ClaimsList";
import { useLineJobDrawer, type LineJobDrawerModel } from "./hooks/useLineJobDrawer";
import { JobStatusBadge, lockedReason } from "./JobStatusBadge";
import { StatusPill } from "./StatusBadge";
import { JOB_INVOICE_STATES, jobInvoiceTitle, listPriceText, nextText, QUOTE_STATUS, rhythmText } from "./lineJobText";
import { PeriodPicker, periodDays, periodError, periodOf, rhythmFrom, type PeriodValue } from "./PeriodPicker";
import { ReviewMark, reviewText } from "./ReviewMark";

const heading = "text-[11px] uppercase tracking-wide text-gray-500 mb-1";
// `contract` names the user's job now (the entityConfig shadow).
const jobWord = ENTITY_CONFIG.contract.label.toLowerCase();

interface Props {
  job: LineJob;
  /** The quote that proposed it, when it has one. */
  quote: QuoteBundle | undefined;
  people: Person[];
  /** The agency's own client (TD-105): its price is what the work costs, and nothing is kept. */
  own: boolean;
  today: string;
  /** The ⋮ by its title: its quote, taking it off one. */
  actions: DropdownMenuEntry[];
  /** Opened from a row's status dropdown picking Active for a job not started: ask its day at once. */
  starting: boolean;
  onOpenQuote: (quoteId: string) => void;
  onOpenInvoice: (invoiceId: string) => void;
  onClose: () => void;
  /** Whose job it is, on /finance's Jobs, as main's agency drawers name their client. */
  titlePrefix?: string;
}

/** A job changes until it is dropped or on an invoice already sent, as the server holds it. */
export const jobEditable = (job: LineJob): boolean => job.status !== "dropped" && (job.invoice === null || job.invoice.status === "draft");

/** It is on a draft or a proposed quote, whose status sets its own. */
export const onOpenQuote = (job: LineJob, quote: QuoteBundle | undefined): boolean => quote !== undefined && job.quoteId === quote.id && (quote.status === "draft" || quote.status === "proposed");

/** The price field's label: the own client's is its cost (TD-105). */
export const priceLabel = (own: boolean, oneOff: boolean): string => `${own ? "Cost" : "Price"}${oneOff ? "" : " this period"}`;

/** The margin % field's hint: the floor, and the ceiling on claims it comes from. */
export const marginHint = (least: number): string => `At least ${least}%: the most the team can claim is ${100 - least}%.`;

/** The hint under Pays all to the team. */
export const PAYS_ALL_HINT = "Setup's ceiling skips it: its margin % may be 0 and the team can claim all of its price.";

/** The hint under List price. */
export const LIST_PRICE_HINT = "Optional, before the discount. With a discount %, it fills the price, which stays editable.";

/** What a list price typed wrong waits for. */
export const LIST_PRICE_ERROR = "A list price above zero, like 800,00";

/** What a discount % typed wrong waits for. */
export const DISCOUNT_ERROR = "A discount above 0 and below 100";

/** The Discount % typed, once it is above 0 and below 100; null otherwise. */
export const discountOf = (text: string): number | null => {
  const n = Number(text);
  return text.trim() !== "" && Number.isFinite(n) && n > 0 && n < 100 ? n : null;
};

/** The price a list price and a discount % fill in: the list price less the discount, rounded to the cent (850,00 at 15%
 * is 722,50). In whole hundredths of a percent, so the rounding works on integers. */
export const discountedMinor = (listMinor: number, pct: number): number => Math.round((listMinor * (10000 - Math.round(pct * 100))) / 10000);

/** When a recurring job or a run of posts turns Done (DD-061: a choice between kinds is a dropdown). A one-off has no
 * choice: someone marks it done. */
export const DONE_WHEN: DropdownOption<JobDoneWhen>[] = [
  { value: "period_end", label: "When the period ends" },
  { value: "marked", label: "When marked done" },
];

/** The hint under Done when. */
export const doneWhenHint = (doneWhen: JobDoneWhen): string =>
  doneWhen === "period_end" ? "It turns Done, ready to invoice, once its last day passes, unless it is paused." : "It stays active until someone marks it Done, ready to invoice.";

/** Active asks the day it starts: a draft, the days it kept from Active or Paused too, an agreed job, and a dropped job
 * with none. */
export const needsStart = (job: LineJob): boolean => job.status === "draft" || job.status === "agreed" || (job.status === "dropped" && job.startsOn === null);

function Figure({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="mt-0.5 text-sm font-semibold tabular-nums text-gray-900">{children}</div>
    </div>
  );
}

type Field = "name" | "description" | "price" | "listPrice" | "discount" | "margin" | "paysAll" | "doneWhen" | "period";

/** The fields edited in place, each saved after the debounce, each refusal kept under its field with what was typed. */
function JobFields({ job, own, least, autosave, update }: { job: LineJob; own: boolean; least: number; autosave: ReturnType<typeof useFieldAutosave<Field>>; update: LineJobDrawerModel["updateJob"] }) {
  const [name, setName] = useState(job.name);
  const [description, setDescription] = useState(job.description ?? "");
  const [price, setPrice] = useState(minorToInput(job.priceMinor));
  const [listPrice, setListPrice] = useState(minorToInput(job.listPriceMinor));
  const [discount, setDiscount] = useState(job.discountPct === null ? "" : String(job.discountPct));
  // A price typed by hand shows its real discount once saved: the server's, read back into Discount %. A discount typed
  // stays as typed, so the next list price is filled with it.
  const priceTyped = useRef(false);
  const [margin, setMargin] = useState(String(job.marginPct));
  const [paysAll, setPaysAll] = useState(job.paysAllToTeam);
  // The box as it reads when a save goes out: the margin % is sent with it, so the server judges the two together and a
  // tick still waiting never leaves a 0% margin judged without it.
  const box = useRef(job.paysAllToTeam);
  const setBox = (on: boolean) => {
    box.current = on;
    setPaysAll(on);
  };
  const [doneWhen, setDoneWhen] = useState<JobDoneWhen>(job.doneWhen);
  const [period, setPeriod] = useState<PeriodValue>(periodOf(job));
  const started = job.startsOn !== null || job.status === "active" || job.status === "paused" || job.status === "done" || job.status === "invoiced";
  const { schedule, hold, clear, errors } = autosave;
  const save = (field: Field, patch: LineJobPatch) => schedule(field, () => update.mutateAsync({ jobId: job.id, patch }));
  // The box is no typed value to keep: refused, it goes back to the server's, and so does the floor; the refusal stays
  // under it until the job reloads with a new margin % or switch.
  const savePaysAll = (on: boolean) =>
    schedule("paysAll", () =>
      update.mutateAsync({ jobId: job.id, patch: { paysAllToTeam: on } }).catch((e: unknown) => {
        setBox(job.paysAllToTeam);
        throw e;
      }),
    );
  useEffect(() => {
    box.current = job.paysAllToTeam;
    setPaysAll(job.paysAllToTeam);
  }, [job.paysAllToTeam]);
  useEffect(() => clear("paysAll"), [job.marginPct, job.paysAllToTeam, clear]);
  useEffect(() => {
    if (priceTyped.current) setDiscount(job.discountPct === null ? "" : String(job.discountPct));
  }, [job.discountPct]);
  // A list price with a discount % fills the price, saved with the list price in one patch so the server judges the two
  // together; a hand-typed price still waiting is dropped, as the filled one replaces it. Cleared, the list price is null.
  const saveListPrice = (listText: string, discountText: string) => {
    priceTyped.current = false;
    if (listText.trim() === "") return save("listPrice", { listPriceMinor: null });
    const list = parseMoneyMinor(listText);
    if (list === null || list <= 0) return hold("listPrice", LIST_PRICE_ERROR);
    const pct = discountOf(discountText);
    if (pct === null) return save("listPrice", { listPriceMinor: list });
    const filled = discountedMinor(list, pct);
    setPrice(minorToInput(filled));
    hold("price", "");
    save("listPrice", { listPriceMinor: list, priceMinor: filled });
  };
  // A rhythm saved as a one-off comes back done when marked: the dropdown reads the server's once it shows again.
  useEffect(() => setDoneWhen(job.doneWhen), [job.doneWhen]);
  const marginEditable = !own && !job.pot;
  // The floor follows the box as it is ticked, before the server has the change.
  const floor = paysAll ? 0 : least;
  return (
    <div className="space-y-4">
      <FormField label="Name" error={errors.name}>
        <input
          aria-label="Name"
          className={inputClassName}
          value={name}
          maxLength={120}
          onChange={(e) => {
            setName(e.target.value);
            if (e.target.value.trim() === "") hold("name", "A name is required");
            else save("name", { name: e.target.value.trim() });
          }}
        />
      </FormField>
      <FormField label="Description" error={errors.description}>
        <textarea
          aria-label="Description"
          rows={2}
          className={inputClassName}
          value={description}
          maxLength={500}
          placeholder="What this period covers, optional"
          onChange={(e) => {
            setDescription(e.target.value);
            save("description", { description: e.target.value.trim() || null });
          }}
        />
      </FormField>
      <PeriodPicker
        value={period}
        dated={started}
        error={errors.period}
        onChange={(next) => {
          setPeriod(next);
          const rhythm = rhythmFrom(next);
          const error = periodError(next, started);
          if (error !== undefined || rhythm === null) return hold("period", error ?? "");
          save("period", started ? { rhythm, ...periodDays(next, true) } : { rhythm });
        }}
      />
      {/* The own client bills nobody (TD-105): it has no list price to discount. */}
      {!own && (
        <div className="grid grid-cols-2 gap-3">
          <FormField label="List price" hint={LIST_PRICE_HINT} error={errors.listPrice}>
            <input
              aria-label="List price"
              inputMode="decimal"
              className={inputClassName}
              value={listPrice}
              placeholder="800,00"
              onChange={(e) => {
                setListPrice(e.target.value);
                saveListPrice(e.target.value, discount);
              }}
            />
          </FormField>
          <FormField label="Discount %" error={errors.discount}>
            <input
              type="number"
              min={0}
              max={100}
              step="any"
              aria-label="Discount %"
              className={inputClassName}
              value={discount}
              onChange={(e) => {
                setDiscount(e.target.value);
                // Not stored: it only fills the price, with a list price to take it from.
                if (e.target.value.trim() !== "" && discountOf(e.target.value) === null) return hold("discount", DISCOUNT_ERROR);
                clear("discount");
                if (discountOf(e.target.value) !== null && listPrice.trim() !== "") saveListPrice(listPrice, e.target.value);
              }}
            />
          </FormField>
        </div>
      )}
      <div className={`grid gap-3 ${marginEditable ? "grid-cols-2" : "grid-cols-1"}`}>
        {/* The own client's price is what the work costs (TD-105), and says so, as its row on /finance's Jobs does. */}
        <FormField label={priceLabel(own, period.mode === "one_off")} hint={listPriceText(job)} error={errors.price}>
          <input
            aria-label="Price"
            inputMode="decimal"
            className={inputClassName}
            value={price}
            onChange={(e) => {
              setPrice(e.target.value);
              priceTyped.current = true;
              const minor = parseMoneyMinor(e.target.value);
              if (minor === null || minor <= 0) hold("price", "A price above zero, like 250,00");
              else save("price", { priceMinor: minor });
            }}
          />
        </FormField>
        {marginEditable && (
          <FormField label="Margin %" hint={errors.margin ? undefined : marginHint(floor)} error={errors.margin}>
            <span className="relative block">
              <input
                type="number"
                min={floor}
                max={100}
                step={1}
                aria-label="Margin %"
                className={`${inputClassName} pr-6 text-right`}
                value={margin}
                onChange={(e) => {
                  setMargin(e.target.value);
                  const n = Number(e.target.value);
                  if (e.target.value.trim() === "" || !Number.isInteger(n) || n < 0 || n > 100) hold("margin", "A whole number from 0 to 100");
                  else schedule("margin", () => update.mutateAsync({ jobId: job.id, patch: { marginPct: n, paysAllToTeam: box.current } }));
                }}
              />
              <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">%</span>
            </span>
          </FormField>
        )}
      </div>
      {/* A one-off is always done when marked: the server forces it, so it has no choice to show. */}
      {period.mode !== "one_off" && (
        <FormField label="Done when" hint={errors.doneWhen ? undefined : doneWhenHint(doneWhen)} error={errors.doneWhen}>
          <OptionDropdown
            variant="input"
            ariaLabel="Done when"
            value={doneWhen}
            options={DONE_WHEN}
            onChange={(next) => {
              setDoneWhen(next);
              save("doneWhen", { doneWhen: next });
            }}
          />
        </FormField>
      )}
      {marginEditable && (
        <div>
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={paysAll}
              onChange={(e) => {
                setBox(e.target.checked);
                savePaysAll(e.target.checked);
              }}
            />
            <span>
              Pays all to the team
              {errors.paysAll ? <span className="block text-xs text-red-600">{errors.paysAll}</span> : <span className="block text-xs text-gray-400">{PAYS_ALL_HINT}</span>}
            </span>
          </label>
        </div>
      )}
    </div>
  );
}

/** The status dropdown, and the day Active asks for a job not started yet. */
function Status({ job, quote, today, starting, aside, set }: { job: LineJob; quote: QuoteBundle | undefined; today: string; starting: boolean; aside: ReactNode; set: LineJobDrawerModel["setStatus"] }) {
  const [asking, setAsking] = useState(starting && needsStart(job));
  const [startsOn, setStartsOn] = useState(job.startsOn ?? today);
  const open = onOpenQuote(job, quote);
  const locked = lockedReason(job, open);
  const oneOff = job.rhythm.mode === "one_off";
  const pick = (status: LineJobPickedStatus) => {
    if (status === "active" && needsStart(job)) return setAsking(true);
    setAsking(false);
    set.mutate({ jobId: job.id, input: { status, startsOn: null } });
  };
  const start = () => set.mutate({ jobId: job.id, input: { status: "active", startsOn: startsOn || null } }, { onSuccess: () => setAsking(false) });
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <JobStatusBadge job={job} onOpenQuote={open} onPick={pick} loading={set.isPending} />
        {aside}
      </div>
      {locked && <p className="text-xs text-gray-500">{locked}</p>}
      {asking && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-gray-50 px-3 py-2">
          <span className="text-xs text-gray-600">{oneOff ? "Its day, optional" : "It starts on"}</span>
          <span className="block w-44">
            <DayField ariaLabel="It starts on" value={startsOn} disabled={set.isPending} onChange={setStartsOn} />
          </span>
          <Button size="sm" disabled={set.isPending || (!oneOff && !startsOn)} isLoading={set.isPending} onClick={start}>
            Start
          </Button>
          <Button size="sm" variant="ghost" disabled={set.isPending} onClick={() => setAsking(false)}>
            Cancel
          </Button>
          {set.error && <p className="w-full text-xs text-red-600">{set.error.message}</p>}
        </div>
      )}
    </div>
  );
}

/** Flag the job's figures to review with an optional note, or clear the flag once they are checked. */
function Review({ job, set }: { job: LineJob; set: LineJobDrawerModel["setReview"] }) {
  const [writing, setWriting] = useState(false);
  const [note, setNote] = useState("");
  const busy = set.isPending;
  const done = () => {
    setWriting(false);
    setNote("");
  };
  if (job.review) {
    return (
      <Callout tone="amber" className="flex items-center justify-between gap-3">
        <span>{reviewText(job.review)}</span>
        <Button size="sm" variant="secondary" disabled={busy} isLoading={busy} onClick={() => set.mutate({ jobId: job.id, input: { review: false, note: null } })}>
          Reviewed
        </Button>
      </Callout>
    );
  }
  if (!writing) {
    return (
      <Button size="sm" variant="ghost" onClick={() => setWriting(true)}>
        Flag the figures to review
      </Button>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      <input
        className={`${inputClassName} min-w-0 flex-1`}
        aria-label="What to review"
        placeholder="What to check, optional"
        maxLength={200}
        value={note}
        disabled={busy}
        onChange={(e) => setNote(e.target.value)}
      />
      <Button size="sm" disabled={busy} isLoading={busy} onClick={() => set.mutate({ jobId: job.id, input: { review: true, note: note.trim() || null } }, { onSuccess: done })}>
        Flag
      </Button>
      <Button size="sm" variant="ghost" disabled={busy} onClick={done}>
        Cancel
      </Button>
    </div>
  );
}

/** The claims on the job (ClaimsList, shared with the invoice drawer): each pressed open to change or remove while it
 * changes, what is free under its cap, and Add a claim. */
function Claims({ job, people, claims, add, update, remove }: { job: LineJob; people: Person[]; claims: LineJobDrawerModel["claims"]; add: LineJobDrawerModel["addClaim"]; update: LineJobDrawerModel["updateClaim"]; remove: LineJobDrawerModel["removeClaim"] }) {
  if (job.pot) return <p className="text-gray-500">{`At cost, back to ${job.pot.label}: nobody claims it.`}</p>;
  const note = (
    <>
      {/* The one claim rule (claimable): a job not under way, or dropped, takes no claim, and says so. */}
      {!job.claimable && job.status !== "dropped" && <p className="text-gray-600">{`${ENTITY_CONFIG.claim.plural} open once the ${jobWord} is active.`}</p>}
      {job.status === "dropped" && <p className="text-gray-600">Dropped: it takes no claim.</p>}
      {job.claimable && (
        <p className={job.overCap ? "text-amber-700" : "text-gray-600"}>
          {job.overCap ? (
            <>
              {"Over the cap: its claims come to "}
              <MoneyText amountMinor={job.claimedMinor} strong />
              {", the cap is "}
              <MoneyText amountMinor={job.capMinor} strong />
              {"."}
            </>
          ) : (
            <>
              {"Free to claim: "}
              <MoneyText amountMinor={job.freeMinor} strong />
              {" of its "}
              <MoneyText amountMinor={job.capMinor} />
              {" cap."}
            </>
          )}
        </p>
      )}
    </>
  );
  return <ClaimsList claims={claims.data ?? []} loading={claims.isLoading} jobs={[job]} people={people} add={add} update={update} remove={remove} note={note} />;
}

export function LineJobDrawer({ job, quote, people, own, today, actions, starting, onOpenQuote, onOpenInvoice, onClose, titlePrefix }: Props) {
  const { settings, claims, addClaim, updateClaim, removeClaim, setStatus, setReview, updateJob } = useLineJobDrawer(job.id);
  const autosave = useFieldAutosave<Field>();
  const editable = jobEditable(job);
  const next = nextText(job);
  const least = own || !settings ? 0 : 100 - settings.maxClaimPct;
  const listText = listPriceText(job);
  return (
    <FinanceDrawer onClose={onClose} title={prefixed(titlePrefix, jobLabel(job.name, job.periodLabel))} titleAside={actions.length > 0 ? <RowMenu items={actions} label={`${job.name} actions`} portal={false} /> : undefined}>
      <div className="space-y-5 text-sm">
        <div className="space-y-1">
          <Status
            job={job}
            quote={quote}
            today={today}
            starting={starting}
            set={setStatus}
            aside={
              <>
                {/* Its period is in the title (jobLabel); how it bills sits by its status. */}
                <span className="text-xs text-gray-500">{rhythmText(job)}</span>
                {autosave.state === "saving" ? (
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500">
                    <SpinnerIcon className="w-3.5 h-3.5 animate-spin" />
                    Saving…
                  </span>
                ) : autosave.state === "saved" ? (
                  <span className="text-xs font-medium text-gray-400">Saved</span>
                ) : null}
              </>
            }
          />
          {next && <p className="text-xs text-gray-500">{next}</p>}
        </div>

        {editable ? (
          // Starting it gives it a period: the fields take its start day from the server.
          <JobFields key={job.startsOn === null ? "not-started" : "started"} job={job} own={own} least={least} autosave={autosave} update={updateJob} />
        ) : (
          job.description && <p className="whitespace-pre-line text-gray-700">{job.description}</p>
        )}

        {/* Edited in place, the price and margin % are their fields: the figures add what the server works out. */}
        <div className={`grid gap-4 ${own || editable ? "grid-cols-2" : "grid-cols-2 sm:grid-cols-4"}`}>
          {!editable && (
            <Figure label="Price">
              <ReviewMark review={job.review}>
                <MoneyText amountMinor={job.priceMinor} />
              </ReviewMark>
              {listText && <span className="block text-xs font-normal text-gray-500">{listText}</span>}
            </Figure>
          )}
          {!own && !editable && <Figure label="Margin %">{`${job.marginPct}%`}</Figure>}
          {!own && !editable && job.paysAllToTeam && <Figure label="All to team">Yes</Figure>}
          <Figure label="Claimed">
            <MoneyText amountMinor={job.claimedMinor} />
          </Figure>
          {!own && (
            <Figure label="Margin">
              <ReviewMark review={job.review}>
                <MoneyText amountMinor={job.marginMinor} balance />
              </ReviewMark>
            </Figure>
          )}
        </div>

        {editable && <Review job={job} set={setReview} />}

        <section>
          <h3 className={heading}>{ENTITY_CONFIG.claim.plural}</h3>
          <Claims job={job} people={people} claims={claims} add={addClaim} update={updateClaim} remove={removeClaim} />
        </section>

        {quote && (
          <section>
            <h3 className={heading}>{ENTITY_CONFIG.quote.label}</h3>
            <button type="button" onClick={() => onOpenQuote(quote.id)} className="w-full flex items-center justify-between gap-3 py-1 text-left hover:bg-gray-50">
              <span className="min-w-0 text-gray-900 [overflow-wrap:anywhere]">{quote.title}</span>
              <StatusPill look={QUOTE_STATUS[quote.status]} ariaLabel={`${quote.title} status`} />
            </button>
          </section>
        )}

        {job.invoice && (
          <section>
            <h3 className={heading}>{ENTITY_CONFIG.invoice.label}</h3>
            <button type="button" onClick={() => job.invoice && onOpenInvoice(job.invoice.id)} className="w-full flex items-center justify-between gap-3 py-1 text-left hover:bg-gray-50">
              <span className="min-w-0">
                <span className="block text-gray-900 [overflow-wrap:anywhere]">{jobInvoiceTitle(job.invoice)}</span>
                <span className="block text-xs text-gray-500">{invoiceDateLine({ kind: "client", ...job.invoice })}</span>
              </span>
              <StatusPill look={JOB_INVOICE_STATES[job.invoice.status]} ariaLabel={`${jobInvoiceTitle(job.invoice)} status`} />
            </button>
          </section>
        )}
      </div>
    </FinanceDrawer>
  );
}
